import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { RotateCcw, Info } from 'lucide-react'
import type { RegimeTemplate, RegimeVariable } from '@/hooks/api/useRegimes'

// =============================================================================
// Props
// =============================================================================

interface RegimeVariableOverridesProps {
  template: RegimeTemplate | null
  overrides: Record<string, number>
  onOverrideChange: (key: string, value: number | undefined) => void
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatValue(value: number, unit: 'percent' | 'bps'): string {
  if (unit === 'bps') {
    return `${value >= 0 ? '+' : ''}${value} bps`
  }
  return `${value >= 0 ? '+' : ''}${value}%`
}

// =============================================================================
// Component
// =============================================================================

export function RegimeVariableOverrides({
  template,
  overrides,
  onOverrideChange,
}: RegimeVariableOverridesProps) {
  if (!template) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-muted-foreground">
          Select a regime template to view and override variables
        </CardContent>
      </Card>
    )
  }

  const hasOverrides = Object.keys(overrides).length > 0

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              Variable Overrides
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p className="text-sm">
                    Override individual regime variables from the template defaults.
                    Changes are applied immediately to the scenario calculation.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
            {hasOverrides && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Reset all overrides
                  Object.keys(overrides).forEach((key) => {
                    onOverrideChange(key, undefined)
                  })
                }}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {template.variables.map((variable) => (
              <VariableInputRow
                key={variable.key}
                variable={variable}
                overrideValue={overrides[variable.key]}
                onChange={(value) => onOverrideChange(variable.key, value)}
              />
            ))}
          </div>

          {/* Override Summary */}
          {hasOverrides && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                <strong>{Object.keys(overrides).length}</strong> variable(s) overridden
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(overrides).map(([key, value]) => {
                  const variable = template.variables.find((v) => v.key === key)
                  if (!variable) return null
                  const isDifferent = value !== variable.defaultValue
                  return (
                    <Badge
                      key={key}
                      variant={isDifferent ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {variable.name}: {formatValue(value, variable.unit)}
                      {isDifferent && ' (modified)'}
                    </Badge>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}

// =============================================================================
// Variable Input Row Component
// =============================================================================

interface VariableInputRowProps {
  variable: RegimeVariable
  overrideValue: number | undefined
  onChange: (value: number | undefined) => void
}

function VariableInputRow({ variable, overrideValue, onChange }: VariableInputRowProps) {
  const currentValue = overrideValue !== undefined ? overrideValue : variable.defaultValue
  const isOverridden = overrideValue !== undefined && overrideValue !== variable.defaultValue

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '') {
      onChange(undefined)
      return
    }
    const numValue = parseFloat(value)
    if (!isNaN(numValue)) {
      onChange(numValue)
    }
  }

  const handleReset = () => {
    onChange(undefined)
  }

  return (
    <div className={`p-3 rounded-lg border ${isOverridden ? 'border-primary bg-primary/5' : 'border-muted'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Label htmlFor={variable.key} className="font-medium">
              {variable.name}
            </Label>
            {isOverridden && (
              <span className="w-2 h-2 rounded-full bg-orange-500" title="Overridden from default" />
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">{variable.description}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Default: {formatValue(variable.defaultValue, variable.unit)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Input
              id={variable.key}
              type="number"
              step={variable.unit === 'bps' ? 1 : 0.1}
              value={currentValue}
              onChange={handleChange}
              className={`w-32 text-right ${isOverridden ? 'border-primary' : ''}`}
            />
            <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {variable.unit === 'bps' ? 'bps' : '%'}
            </span>
          </div>
          {isOverridden && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleReset}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
